import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Navbar from './Navbar';
import './Empleados.css'; // Reuse the same CSS as Empleados since it's the same view with different characteristics
import { FaSearch } from 'react-icons/fa';
import { MdDelete } from "react-icons/md";
import { IoMdPersonAdd } from "react-icons/io";
import EditEmployeeModal from './EditEmployeeModal';
import CreateUserModal from './CreateUserModal';

function Reactivacion() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const ROWS_PER_PAGE = 10;
    const [searchTerm, setSearchTerm] = useState('');
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [isPrevDisabled, setIsPrevDisabled] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [userRole, setUserRole] = useState('');
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [reactivatedEmployeeEmail, setReactivatedEmployeeEmail] = useState('');

    const isLoadingRef = useRef(false);
    const searchTimeoutRef = useRef(null);

    // Function to get the user's role
    const getUserRole = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const { data, error } = await supabase
                    .from('users')
                    .select('role')
                    .eq('wsp_email', user.email)
                    .single();

                if (error) throw error;
                setUserRole(data.role);
            }
        } catch (error) {
            console.error('Error fetching user role:', error.message);
        }
    };

    // Fetch user role when component mounts
    useEffect(() => {
        getUserRole();
    }, []);

    // Function to handle reactivating an employee
    const handleReactivate = (employee) => {
        setEditingEmployee({
            ...employee,
            days_taken: 0,
            remaining_days: 0,
            date_of_entry: '',
            renovation_day: ''
        });
    };

    // Function to handle save and reactivate employee
    const handleSaveAndReactivate = async (updatedData, createUserAccount = false) => {
        try {
            // First reset overtime vacation days
            const { error: overtimeError } = await supabase
                .from('overtime_vacation_days')
                .update({
                    days_remaining: null,
                    expiration_date: null
                })
                .eq('employee_id', editingEmployee.id);

            if (overtimeError) throw overtimeError;

            // Then update employee data
            const { error: employeeError } = await supabase
                .from('employees')
                .update({
                    ...updatedData,
                    active: true,
                    days_taken: 0
                })
                .eq('id', editingEmployee.id);

            if (employeeError) throw employeeError;

            // Close the edit modal
            setEditingEmployee(null);

            // If user checked the create user account toggle, show the create user modal
            if (createUserAccount) {
                setReactivatedEmployeeEmail(updatedData.wsp_email);
                setShowCreateUserModal(true);
            } else {
                // Refresh the employee list if not creating a user
                fetchEmployees(currentPage, searchTerm);
            }
        } catch (error) {
            setError('Error updating employee: ' + error.message);
        }
    };

    // Add a function to handle user creation completion
    const handleUserCreated = async (userData) => {
        setShowCreateUserModal(false);
        // Refresh the list after user creation
        fetchEmployees(currentPage, searchTerm);
        return true;
    };

    // Function to permanently delete an employee
    const handleDelete = async (employee) => {
        try {
            const { error } = await supabase
                .from('employees')
                .delete()
                .eq('id', employee.id);

            if (error) throw error;
            fetchEmployees(currentPage, searchTerm);
        } catch (error) {
            console.error('Delete error:', error);
            setError('Error deleting employee: ' + error.message);
        }
    };

    // Fetch inactive employees from the database
    const fetchEmployees = useCallback(async (page = 0, search = '') => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;

        setIsPageLoading(true);
        setLoading(true);

        try {
            const from = page * ROWS_PER_PAGE;
            const to = from + ROWS_PER_PAGE - 1;

            // Apply role-based filtering
            let query = supabase
                .from('employees')
                .select('*', { count: 'exact' })
                .eq('active', false);

            // Filter based on user role
            if (userRole === 'admin' || userRole === 'RH') {
                // No additional filter - can see all employees
            } else if (userRole === 'CEO') {
                query = query.in('area', ['Administrativo / Dirección', 'Comercial', 'Ventas']);
            } else if (['Fábrica ABAP', 'Staffing', 'PMO-IT', 'Finanzas'].includes(userRole)) {
                // For these roles, they can only see employees in their own area
                query = query.eq('area', userRole);
            }

            // Apply search filter if any
            if (search) {
                query = query.ilike('wsp_email', `%${search}%`);
            }

            // First, get the total count with the same filters
            const { data, error: countError, count } = await query
                .range(0, 0) // Just get the first record for counting
                .limit(1);   // Limit to 1 record for efficiency

            if (countError) throw countError;
            setTotalCount(count || 0);

            if (count === 0 || from >= count) {
                setEmployees([]);
                setIsNextDisabled(true);
                setIsPrevDisabled(page === 0);
                setHasMore(false);
                setLoading(false);
                setIsPageLoading(false);
                isLoadingRef.current = false;
                return;
            }

            // Fetch employees
            const { data: fetchedEmployees, error } = await query
                .range(from, to)
                .order('id', { ascending: true });

            if (error) throw error;

            setEmployees(fetchedEmployees);
            setIsNextDisabled(from + fetchedEmployees.length >= count);
            setIsPrevDisabled(page === 0);
            setHasMore(from + fetchedEmployees.length < count);
            setError(null);

        } catch (error) {
            console.error('Error:', error);
            setError('Error fetching employees: ' + error.message);
        } finally {
            setLoading(false);
            setIsPageLoading(false);
            isLoadingRef.current = false;
        }
    }, [userRole]); // Add userRole as dependency

    // Pagination handlers
    const handlePreviousPage = useCallback(() => {
        if (currentPage > 0 && !isPrevDisabled && !isPageLoading && !isLoadingRef.current) {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            fetchEmployees(newPage, searchTerm);
        }
    }, [currentPage, isPrevDisabled, isPageLoading, searchTerm, fetchEmployees]);

    const handleNextPage = useCallback(() => {
        if (hasMore && !isNextDisabled && !isPageLoading && !isLoadingRef.current) {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            fetchEmployees(newPage, searchTerm);
        }
    }, [currentPage, hasMore, isNextDisabled, isPageLoading, searchTerm, fetchEmployees]);

    // Search handler
    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    }, []);

    // Search effect - only run when userRole is available
    useEffect(() => {
        if (userRole) {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }

            searchTimeoutRef.current = setTimeout(() => {
                fetchEmployees(0, searchTerm);
            }, 300);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, fetchEmployees, userRole]);

    return (
        <div className="empleados">
            <Navbar />
            <div className="empleados-content">
                <div className="empleados-header">
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <FaSearch className="search-icon" />
                            <input
                                type="search"
                                placeholder="Buscar empleado por correo WSP"
                                className="search-input"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="table-container">
                    {error && <div className="error-message">{error}</div>}
                    {loading ? (
                        <div className="loading-message">Cargando empleados...</div>
                    ) : (
                        <table className="empleados-table">
                            <thead>
                                <tr>
                                    <th>Nombre Completo</th>
                                    <th>Correo WSP</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map((employee) => (
                                    <tr key={employee.wsp_email}>
                                        <td className="inactive-name">{employee.full_name}</td>
                                        <td className="inactive-name">{employee.wsp_email}</td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="edit-button"
                                                    title="Reactivar"
                                                    onClick={() => handleReactivate(employee)}
                                                >
                                                    <IoMdPersonAdd className="reactivate-icon" />
                                                </button>

                                                <button
                                                    className="delete-button"
                                                    title="Eliminar Permanentemente"
                                                    onClick={() => {
                                                        if (window.confirm('¿Está seguro que desea eliminar permanentemente este empleado?\n\n'
                                                            + 'Esta acción no se puede deshacer.\n\n'
                                                            + employee.full_name + ' - ' + employee.wsp_email)) {
                                                            handleDelete(employee);
                                                        }
                                                    }}
                                                >
                                                    <MdDelete className="delete-icon" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            {/* Add EditEmployeeModal */}
                            {editingEmployee && (
                                <EditEmployeeModal
                                    employee={editingEmployee}
                                    onClose={() => setEditingEmployee(null)}
                                    onSave={handleSaveAndReactivate}
                                    isReactivating={true}  // New prop to indicate reactivation
                                />
                            )}
                        </table>
                    )}
                </div>

                <div className="pagination">
                    <button
                        className={`pagination-button ${isPrevDisabled ? 'disabled' : ''}`}
                        onClick={handlePreviousPage}
                        disabled={isPrevDisabled || isPageLoading}
                    >
                        Atrás
                    </button>
                    <span className="page-info">
                        Página {currentPage + 1} de {Math.ceil(totalCount / ROWS_PER_PAGE)}
                    </span>
                    <button
                        className={`pagination-button ${isNextDisabled ? 'disabled' : ''}`}
                        onClick={handleNextPage}
                        disabled={isNextDisabled || isPageLoading}
                    >
                        Siguiente
                    </button>
                </div>

                {/* Add CreateUserModal */}
                {showCreateUserModal && (
                    <CreateUserModal
                        onClose={() => {
                            setShowCreateUserModal(false);
                            fetchEmployees(currentPage, searchTerm);
                        }}
                        onSave={handleUserCreated}
                        userType="Empleado"
                        prefilledEmail={reactivatedEmployeeEmail}
                    />
                )}
            </div>
        </div>
    );
}

export default Reactivacion;